// Utility functions for photometric calculations

/**
 * Convert intensity (0-1) to approximate lux values
 * This is a simplified conversion - in reality, lux depends on distance, light source, etc.
 * @param {number} intensity - Intensity value from 0 to 1
 * @param {number} maxValue - Maximum lux value for the scale
 * @returns {number} Approximate lux value
 */
export const intensityToLux = (intensity, maxValue = 2000) => {
  // Use the configurable max value for conversion
  return Math.round(intensity * maxValue);
};

/**
 * Convert intensity (0-1) to approximate lumens
 * @param {number} intensity - Intensity value from 0 to 1
 * @returns {number} Approximate lumens value
 */
export const intensityToLumens = (intensity) => {
  // Simplified conversion: assume max intensity corresponds to ~10000 lumens
  return Math.round(intensity * 10000);
};

/**
 * Convert hue value to RGB color
 * @param {number} hue - Hue value from 0 to 1
 * @returns {string} Hex color string
 */
export const hueToRGB = (hue) => {
  // Convert hue to RGB using the same algorithm as the shader
  const h = hue * 6;
  const c = 1;
  const x = c * (1 - Math.abs((h % 2) - 1));
  const m = 0;

  let r, g, b;
  if (h < 1) {
    r = c;
    g = x;
    b = 0;
  } else if (h < 2) {
    r = x;
    g = c;
    b = 0;
  } else if (h < 3) {
    r = 0;
    g = c;
    b = x;
  } else if (h < 4) {
    r = 0;
    g = x;
    b = c;
  } else if (h < 5) {
    r = x;
    g = 0;
    b = c;
  } else {
    r = c;
    g = 0;
    b = x;
  }

  // Convert to hex
  const toHex = (c) => {
    const hex = Math.round((c + m) * 255).toString(16);
    return hex.length === 1 ? '0' + hex : hex;
  };

  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
};

/**
 * Generate photometric spectrum data for the table
 * @param {boolean} steppedColors - Whether to use stepped colors or smooth gradient
 * @param {number} maxLuxValue - Maximum lux value (cutoff threshold)
 * @returns {Array} Array of spectrum data objects
 */
export const generatePhotometricSpectrum = (steppedColors = false, maxLuxValue = 2000) => {
  const spectrum = [];
  const steps = 9; // Number of steps for the spectrum table (excluding 0)
  // Define stepped color palette to match shader (under threshold + over threshold)
  const steppedColorPalette = [
    '#6680B3', // Blue
    '#80B3B3', // Teal/cyan
    '#99E6B3', // Light green/mint
    '#F2F2B3', // Light yellow/cream
    '#FFFF80', // Yellow
    '#FFCC80', // Light orange/peach
    '#FF9966', // Orange
    '#E6664D', // Red-orange/coral
    '#B32626', // Red (brightest)
  ];

  for (let i = 1; i <= steps; i++) {
    let luxValue,
      intensity,
      isOverThreshold = false;

    if (i === steps) {
      // Last step represents over-threshold values
      luxValue = Math.round(maxLuxValue * 1.2); // Show 120% of threshold as example
      intensity = 1.0;
      isOverThreshold = true;
    } else {
      // Linear distribution of values under threshold (starting from >0)
      const percentage = i / (steps - 1);
      luxValue = Math.round(maxLuxValue * percentage);
      intensity = percentage * 0.9; // Map to 0-0.9 range
    }

    let color;
    if (steppedColors) {
      // Use predefined stepped colors
      color = steppedColorPalette[i - 1];
    } else {
      // Use smooth gradient mapping
      if (isOverThreshold) {
        color = '#FF0000'; // Pure red for over-threshold
      } else {
        const hue = (1.0 - intensity / 0.9) * 0.8; // Map 0-0.9 intensity to full hue range
        color = hueToRGB(hue);
      }
    }

    const lumens = intensityToLumens(intensity);
    const description = isOverThreshold ? 'Over threshold' : getIntensityDescription(intensity);

    spectrum.push({
      intensity: intensity.toFixed(2),
      color,
      lux: luxValue,
      lumens,
      description,
      isOverThreshold,
    });
  }

  return spectrum;
};

/**
 * Get a human-readable description of the intensity level
 * @param {number} intensity - Intensity value from 0 to 1
 * @returns {string} Description of the intensity level
 */
const getIntensityDescription = (intensity) => {
  if (intensity <= 0.1) return 'Very dim';
  if (intensity <= 0.2) return 'Dim';
  if (intensity <= 0.3) return 'Low';
  if (intensity <= 0.4) return 'Moderate';
  if (intensity <= 0.5) return 'Medium';
  if (intensity <= 0.6) return 'Bright';
  if (intensity <= 0.7) return 'Very bright';
  if (intensity <= 0.8) return 'Intense';
  if (intensity <= 0.9) return 'Very intense';
  return 'Maximum';
};
